import { Button } from "@/components/ui/button";
import { Card, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { DialogClose } from "@/components/ui/dialog";
import { ChevronLeft } from "lucide-react";
import { OperationProgressItem } from "@/components/progress";
import { useTranslation } from "react-i18next";
import { AsyncOperationItemList } from "./AsyncOperationItemList";

interface AsyncOperationCompletedStepProps {
  processedItems: number;
  totalItems: number;
  data: OperationProgressItem[];
  completedTitleKey: string;
  completedDescriptionKey: string;
  completedGoBackButtonKey: string;
  completedCloseButtonKey: string;
  onReset?: () => void;
  onClose?: () => void;
  renderCompletedItem?: (item: OperationProgressItem) => React.ReactNode;
  i18nMetadata?: Record<string, unknown>;
}

export const AsyncOperationCompletedStep = ({
  processedItems,
  totalItems,
  data,
  onReset,
  completedTitleKey,
  completedDescriptionKey,
  completedGoBackButtonKey,
  completedCloseButtonKey,
  onClose,
  renderCompletedItem,
  i18nMetadata,
}: AsyncOperationCompletedStepProps) => {
  const { t } = useTranslation();

  return (
    <Card variant="ghost" className="p-0">
      <CardHeader className="p-0">
        <CardTitle>
          {t(completedTitleKey, {
            processed: processedItems,
            total: totalItems,
            ...i18nMetadata,
          })}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t(completedDescriptionKey, {
            processed: processedItems,
            total: totalItems,
            count: totalItems,
            ...i18nMetadata,
          })}
        </p>
      </CardHeader>
      <AsyncOperationItemList
        data={data}
        renderCompletedItem={renderCompletedItem}
      />
      <CardFooter className="gap-3 p-0 flex justify-between">
        <Button
          onClick={onReset}
          variant="secondary"
          className={onReset ? "" : "invisible"}
        >
          <ChevronLeft className="w-4 h-4 inline" />
          {t(completedGoBackButtonKey)}
        </Button>
        <DialogClose asChild>
          <Button onClick={onClose} data-testid="close">
            {t(completedCloseButtonKey)}
          </Button>
        </DialogClose>
      </CardFooter>
    </Card>
  );
};
